import { z } from 'zod';

/* ─────────── Проектная документация: комплекты, листы, ревизии, накладные (раздел 6.4) ─────────── */

const DateStr = z.iso.date();

/** Стадии проектирования (ПД-01) */
export const PSD_STAGES = {
  П: 'Проект',
  РП: 'Рабочий проект',
  Р: 'Рабочая документация',
  ЭП: 'Эскизный проект',
} as const;
export type PsdStage = keyof typeof PSD_STAGES;

/** Марки комплектов по умолчанию: правило шифра проекта может их дополнить (ПД-23) */
export const DEFAULT_MARKS = [
  'ПЗ',
  'ГП',
  'ГТ',
  'АР',
  'АС',
  'КЖ',
  'КЖИ',
  'КМ',
  'КМД',
  'КР',
  'ТХ',
  'ТМ',
  'ВК',
  'НВ',
  'НВК',
  'НК',
  'ОВ',
  'ТС',
  'ЭОМ',
  'ЭС',
  'ЭН',
  'ЭМ',
  'ЭХЗ',
  'АТХ',
  'АК',
  'АПС',
  'СС',
  'ПОС',
  'ООС',
  'ПБ',
  'СМ',
] as const;

/** Статусы ревизии (ПД-04) */
export const REVISION_STATUSES = {
  development: 'В разработке',
  review: 'На проверке',
  remarks: 'Замечания',
  approved: 'Утверждено',
  production: 'В производство работ',
  annulled: 'Аннулировано',
} as const;
export type RevisionStatus = keyof typeof REVISION_STATUSES;

/** Состояние комплекта: карантин, пока шифр не проходит правило (ПД-23) */
export const SET_STATES = {
  ok: 'Шифр проверен',
  quarantine: 'Карантин загрузки',
} as const;
export type SetState = keyof typeof SET_STATES;

/** Код ревизии R00, R01, … */
export function revisionCode(index: number): string {
  return `R${String(index).padStart(2, '0')}`;
}

/** Правило шифра комплекта на проект (ПД-23): «префикс — стадия — объект — марка[номер]» */
export interface CodeRule {
  /** Начало шифра: номер договора проекта, например «227-23» */
  prefix: string;
  separator: string;
  stages: string[];
  marks: string[];
  /** Объект в шифре должен совпасть с кодом объекта проекта (ПД-12) */
  checkObject: boolean;
}

export const CodeRuleInput = z.object({
  prefix: z.string().trim().max(40),
  separator: z.string().min(1).max(3),
  stages: z.array(z.string().trim().min(1).max(10)).min(1).max(20),
  marks: z.array(z.string().trim().min(1).max(12)).min(1).max(200),
  checkObject: z.boolean(),
});
export type CodeRuleInput = z.infer<typeof CodeRuleInput>;

export interface ParsedCode {
  prefix: string | null;
  stage: string | null;
  object: string | null;
  mark: string | null;
  /** Номер внутри марки: КЖ1, КЖ-2 → «1», «2» */
  markNo: string | null;
  /** Код объекта проекта, с которым совпал объект шифра */
  objectCode: string | null;
  problems: string[];
}

/**
 * Разбор шифра комплекта (ПД-01, ПД-12, ПД-23): 227-23-РП-ВВ-1-КЖ → префикс 227-23, стадия РП,
 * объект ВВ-1, марка КЖ. Объект может содержать разделитель, поэтому стадия ищется по справочнику,
 * а марка — с конца. Объект сверяется с кодами объектов проекта: «ОХЗ-1» вместо «ОЗХ-1» — ошибка.
 * Одна функция для сервера и для формы загрузки.
 */
export function parseSetCode(code: string, rule: CodeRule, objectCodes: string[]): ParsedCode {
  const out: ParsedCode = {
    prefix: null,
    stage: null,
    object: null,
    mark: null,
    markNo: null,
    objectCode: null,
    problems: [],
  };
  const sep = rule.separator || '-';
  const raw = code.trim();
  if (!raw) {
    out.problems.push('Шифр не заполнен');
    return out;
  }
  let rest = raw;
  if (rule.prefix) {
    if (!raw.startsWith(rule.prefix + sep)) {
      out.problems.push(`Шифр должен начинаться с «${rule.prefix}${sep}»`);
    } else {
      out.prefix = rule.prefix;
      rest = raw.slice(rule.prefix.length + sep.length);
    }
  }
  const parts = rest.split(sep).filter((p) => p.length > 0);
  const stageIdx = parts.findIndex((p) => rule.stages.includes(p));
  if (stageIdx < 0) {
    out.problems.push(`Нет стадии из справочника: ${rule.stages.join(', ')}`);
    return out;
  }
  if (!out.prefix && stageIdx > 0 && !rule.prefix) out.prefix = parts.slice(0, stageIdx).join(sep);
  out.stage = parts[stageIdx]!;
  const tail = parts.slice(stageIdx + 1);
  if (!tail.length) {
    out.problems.push('После стадии нет объекта и марки');
    return out;
  }
  // Марка — последний элемент; номер марки может идти слитно (КЖ1) или отдельным элементом (КЖ-1)
  const marks = [...rule.marks].sort((a, b) => b.length - a.length);
  const matchMark = (s: string) => {
    for (const m of marks) {
      if (s === m) return { mark: m, no: null as string | null };
      if (s.startsWith(m) && /^\d+[а-яa-z]?$/i.test(s.slice(m.length)))
        return { mark: m, no: s.slice(m.length) };
    }
    return null;
  };
  let markAt = tail.length - 1;
  let found = matchMark(tail[markAt]!);
  if (!found && tail.length >= 2 && /^\d+$/.test(tail[markAt]!)) {
    const m = matchMark(tail[markAt - 1]!);
    if (m && m.no === null) {
      found = { mark: m.mark, no: tail[markAt]! };
      markAt -= 1;
    }
  }
  if (!found) {
    out.problems.push(`Марка «${tail[tail.length - 1]}» не из справочника марок`);
  } else {
    out.mark = found.mark;
    out.markNo = found.no;
  }
  const objParts = found ? tail.slice(0, markAt) : tail.slice(0, -1);
  if (!objParts.length) {
    out.problems.push('В шифре нет объекта');
  } else {
    out.object = objParts.join(sep);
    if (rule.checkObject && objectCodes.length) {
      const hit = objectCodes
        .filter(Boolean)
        .sort((a, b) => b.length - a.length)
        .find(
          // Совпадение целиком или по хвосту «…-ОЗХ-1»; хвост из одних цифр не считается объектом («ВВ-1» ≠ «1»)
          (c) => out.object === c || (/\p{L}/u.test(c) && out.object!.endsWith(sep + c)),
        );
      if (hit) out.objectCode = hit;
      else
        out.problems.push(
          `Объект «${out.object}» не совпадает с объектами проекта: ${objectCodes.join(', ')}`,
        );
    }
  }
  return out;
}

/** Номер листа: «1», «2а», «12.1» */
const SheetNo = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .regex(/^[0-9A-Za-zА-Яа-яЁё.\-/]+$/, 'Номер листа — цифры и буквы без пробелов')
  .regex(/^(?!.*\d{6})/, 'Номер листа слишком длинный');

export const SetInput = z.object({
  projectId: z.uuid(),
  code: z.string().trim().min(3).max(120),
  name: z.string().trim().min(2).max(300),
  designerId: z.uuid().nullable().optional(),
  plannedDate: DateStr.nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  /** Ведомость листов: номер и наименование (ПД-12 — нет пропусков и дублей) */
  sheets: z
    .array(z.object({ number: SheetNo, name: z.string().trim().min(1).max(300) }))
    .max(2000)
    .optional(),
});
export type SetInput = z.infer<typeof SetInput>;

export const SetPatch = z.object({
  code: z.string().trim().min(3).max(120).optional(),
  name: z.string().trim().min(2).max(300).optional(),
  designerId: z.uuid().nullable().optional(),
  plannedDate: DateStr.nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});

export const SheetsInput = z.object({
  sheets: z
    .array(z.object({ number: SheetNo, name: z.string().trim().min(1).max(300) }))
    .min(1)
    .max(2000),
});

export const RevisionInput = z.object({
  note: z.string().trim().max(2000).nullable().optional(),
});

export const REVISION_ACTIONS = {
  submit: 'Отправить на проверку',
  remarks: 'Вернуть с замечаниями',
  approve: 'Утвердить',
  stamp: 'Штамп «В производство работ»',
  annul: 'Аннулировать',
} as const;
export type RevisionAction = keyof typeof REVISION_ACTIONS;

export const RevisionActionInput = z.object({
  action: z.enum(Object.keys(REVISION_ACTIONS) as [RevisionAction, ...RevisionAction[]]),
  comment: z.string().trim().max(2000).nullable().optional(),
  /** Дата штампа «В производство работ» (ПД-05) */
  date: DateStr.optional(),
});

export const RevisionSheetView = z.object({
  sheetId: z.uuid(),
  number: z.string(),
  name: z.string(),
  changeNo: z.string().nullable(),
  fileId: z.uuid().nullable(),
  fileName: z.string().nullable(),
  page: z.number().nullable(),
  /** Лист изменён в этой ревизии (новый файл) */
  changed: z.boolean(),
  openQuestions: z.number(),
  openRemarks: z.number(),
});
export type RevisionSheetView = z.infer<typeof RevisionSheetView>;

export const RevisionView = z.object({
  id: z.uuid(),
  code: z.string(),
  index: z.number(),
  status: z.enum(Object.keys(REVISION_STATUSES) as [RevisionStatus, ...RevisionStatus[]]),
  note: z.string().nullable(),
  stampDate: z.string().nullable(),
  stampedBy: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  files: z.array(z.object({ id: z.uuid(), name: z.string(), sizeBytes: z.number(), mimeType: z.string() })),
  sheets: z.array(RevisionSheetView),
  events: z.array(
    z.object({
      at: z.string(),
      by: z.string().nullable(),
      action: z.string(),
      comment: z.string().nullable(),
    }),
  ),
});
export type RevisionView = z.infer<typeof RevisionView>;

export const SetSummary = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  code: z.string(),
  name: z.string(),
  stage: z.string().nullable(),
  mark: z.string().nullable(),
  /** Номер альбома внутри марки: КЖ1, КЖ-2 → «1», «2» */
  markNo: z.string().nullable(),
  object: z.string().nullable(),
  /** Объект (пятно) проекта — узел дерева pm.project_objects; null — «Без объекта» */
  objectId: z.string().nullable(),
  state: z.enum(['ok', 'quarantine']),
  problems: z.array(z.string()),
  designerName: z.string().nullable(),
  plannedDate: z.string().nullable(),
  receivedDate: z.string().nullable(),
  /** Комплект просрочен проектировщиком (ПД-16) */
  overdue: z.boolean(),
  sheetCount: z.number(),
  /** Действующая ревизия со штампом «В производство работ» */
  activeRevision: z.string().nullable(),
  /** Последняя ревизия (видна тем, кто проверяет и загружает) */
  lastRevision: z.string().nullable(),
  lastStatus: z.string().nullable(),
  openQuestions: z.number(),
  openRemarks: z.number(),
});
export type SetSummary = z.infer<typeof SetSummary>;

export const SetDetail = SetSummary.extend({
  designerId: z.uuid().nullable(),
  note: z.string().nullable(),
  objectCode: z.string().nullable(),
  sheets: z.array(z.object({ id: z.uuid(), number: z.string(), name: z.string() })),
  /** Пропуски и дубли в ведомости листов (ПД-12) */
  sheetProblems: z.array(z.string()),
  revisions: z.array(RevisionView),
  /** Видит ли человек только действующую ревизию (прораб, подрядчик — ПД-05) */
  activeOnly: z.boolean(),
  transmittals: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      direction: z.string(),
      date: z.string(),
      party: z.string().nullable(),
    }),
  ),
});
export type SetDetail = z.infer<typeof SetDetail>;

/** Проверка ведомости листов: пропуски номеров и дубли (ПД-12) */
export function sheetListProblems(numbers: string[]): string[] {
  const out: string[] = [];
  const seen = new Map<string, number>();
  for (const n of numbers) seen.set(n, (seen.get(n) ?? 0) + 1);
  const dups = [...seen].filter(([, c]) => c > 1).map(([n]) => n);
  if (dups.length) out.push(`Дубли листов: ${dups.join(', ')}`);
  const ints = [...new Set(numbers.filter((n) => /^\d{1,5}$/.test(n)).map(Number))].sort((a, b) => a - b);
  if (ints.length) {
    const missing: number[] = [];
    const have = new Set(ints);
    const top = Math.min(ints[ints.length - 1]!, 5000);
    for (let i = 1; i < top; i++) if (!have.has(i)) missing.push(i);
    if (missing.length)
      out.push(
        `Пропущены листы: ${missing.slice(0, 20).join(', ')}${missing.length > 20 ? ` и ещё ${missing.length - 20}` : ''}`,
      );
  }
  return out;
}

/* ─────────── Накладные: входящая от проектировщика (ПД-13), исходящая (ПД-24) ─────────── */

export const TRANSMITTAL_DIRECTIONS = {
  in: 'Входящая от проектировщика',
  out: 'Исходящая',
} as const;
export type TransmittalDirection = keyof typeof TRANSMITTAL_DIRECTIONS;

export const TRANSMITTAL_STATUSES = {
  draft: 'Черновик',
  sent: 'Отправлена',
  received: 'Получена',
} as const;
export type TransmittalStatus = keyof typeof TRANSMITTAL_STATUSES;

export const RECIPIENT_KINDS = {
  customer: 'Заказчик',
  tech_supervision: 'Технадзор',
  contractor: 'Подрядчик',
  expertise: 'Экспертиза',
  designer: 'Проектировщик',
  site: 'Своя площадка',
} as const;
export type RecipientKind = keyof typeof RECIPIENT_KINDS;

export const TransmittalInput = z.object({
  projectId: z.uuid(),
  direction: z.enum(['in', 'out']),
  date: DateStr,
  counterpartyId: z.uuid().nullable().optional(),
  recipientKind: z
    .enum(Object.keys(RECIPIENT_KINDS) as [RecipientKind, ...RecipientKind[]])
    .nullable()
    .optional(),
  /** Номер накладной проектировщика (входящая) */
  externalNumber: z.string().trim().max(60).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  items: z
    .array(
      z.object({
        revisionId: z.uuid(),
        copies: z.number().int().min(0).max(100),
        paper: z.boolean(),
      }),
    )
    .min(1, 'Добавьте хотя бы один комплект')
    .max(500),
});
export type TransmittalInput = z.infer<typeof TransmittalInput>;

export const TransmittalView = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectCode: z.string(),
  number: z.string(),
  direction: z.enum(['in', 'out']),
  status: z.enum(['draft', 'sent', 'received']),
  date: z.string(),
  counterpartyId: z.uuid().nullable(),
  party: z.string().nullable(),
  recipientKind: z.string().nullable(),
  externalNumber: z.string().nullable(),
  note: z.string().nullable(),
  receivedAt: z.string().nullable(),
  receivedBy: z.string().nullable(),
  createdBy: z.string().nullable(),
  items: z.array(
    z.object({
      setId: z.uuid(),
      setCode: z.string(),
      setName: z.string(),
      revisionId: z.uuid(),
      revision: z.string(),
      revisionStatus: z.string(),
      sheetCount: z.number(),
      copies: z.number(),
      paper: z.boolean(),
    }),
  ),
});
export type TransmittalView = z.infer<typeof TransmittalView>;

/** Номер накладной: НК-ВХ-0001 / НК-ИСХ-0001 по проекту */
export function transmittalNumber(direction: TransmittalDirection, n: number): string {
  return `НК-${direction === 'in' ? 'ВХ' : 'ИСХ'}-${String(n).padStart(4, '0')}`;
}

/** Журнал просмотров и скачиваний листов (ПД-17) */
export const AccessLogRow = z.object({
  at: z.string(),
  user: z.string().nullable(),
  action: z.enum(['view', 'download']),
  file: z.string(),
  set: z.string(),
  revision: z.string(),
});
export type AccessLogRow = z.infer<typeof AccessLogRow>;

/* ─────────── Пометки на листе и точки вопросов и замечаний (ПД-10, ПД-26) ─────────── */

export const MARKUP_KINDS = {
  cloud: 'Облако',
  arrow: 'Стрелка',
  text: 'Текст',
  dimension: 'Размер',
} as const;
export type MarkupKind = keyof typeof MARKUP_KINDS;

const Frac = z.number().min(0).max(1);

export const MarkupInput = z.object({
  revisionId: z.uuid(),
  page: z.number().int().min(1).max(10000).optional(),
  kind: z.enum(Object.keys(MARKUP_KINDS) as [MarkupKind, ...MarkupKind[]]),
  geometry: z.object({ x1: Frac, y1: Frac, x2: Frac, y2: Frac }),
  text: z.string().trim().max(1000).nullable().optional(),
  color: z.enum(['red', 'blue', 'green', 'orange']).optional(),
});
export type MarkupInput = z.infer<typeof MarkupInput>;

export const MarkupView = z.object({
  id: z.uuid(),
  revisionId: z.uuid(),
  page: z.number(),
  kind: z.enum(Object.keys(MARKUP_KINDS) as [MarkupKind, ...MarkupKind[]]),
  geometry: z.object({ x1: z.number(), y1: z.number(), x2: z.number(), y2: z.number() }),
  text: z.string().nullable(),
  color: z.string(),
  published: z.boolean(),
  status: z.enum(['open', 'closed']),
  recheck: z.boolean(),
  author: z.string().nullable(),
  mine: z.boolean(),
  /** Править можно: своя, ревизия не погашена */
  editable: z.boolean(),
  createdAt: z.string(),
});
export type MarkupView = z.infer<typeof MarkupView>;

export const SheetPin = z.object({
  type: z.enum(['question', 'remark']),
  id: z.uuid(),
  number: z.string(),
  title: z.string(),
  status: z.string(),
  open: z.boolean(),
  x: z.number(),
  y: z.number(),
});
export type SheetPin = z.infer<typeof SheetPin>;

/** Загрузка частями (ПД-02): до 2 ГБ, части по 16 МБ, докачка с места обрыва */
export const PSD_MAX_FILE = 2 * 1024 * 1024 * 1024;
export const PSD_PART_SIZE = 16 * 1024 * 1024;

export const UploadStart = z.object({
  name: z.string().trim().min(1).max(255),
  size: z.number().int().min(1).max(PSD_MAX_FILE),
  sheetId: z.uuid().nullable().optional(),
  /** Отпечаток файла: размер и время изменения — чтобы докачка не смешала два разных файла */
  clientKey: z.string().max(100).optional(),
});

export const UploadState = z.object({
  id: z.uuid(),
  name: z.string(),
  size: z.number(),
  partSize: z.number(),
  parts: z.array(z.number()),
  status: z.enum(['open', 'done', 'aborted']),
});
export type UploadState = z.infer<typeof UploadState>;

/* ─────────── Рассылка ревизии с отметкой прочтения (ПД-11) ─────────── */

export const DistributionInput = z.object({ userIds: z.array(z.uuid()).min(1).max(200) });
export type DistributionInput = z.infer<typeof DistributionInput>;

export const DistributionRow = z.object({
  userId: z.uuid(),
  fullName: z.string(),
  external: z.boolean(),
  sentAt: z.string(),
  readAt: z.string().nullable(),
  reminders: z.number(),
  remindedAt: z.string().nullable(),
});
export type DistributionRow = z.infer<typeof DistributionRow>;

export const SetDistribution = z.object({
  /** Может разослать и напомнить: тот, кто утверждает или ставит штамп */
  canSend: z.boolean(),
  /** Мне разослано: ревизии, с которыми надо ознакомиться */
  mine: z.array(
    z.object({
      revisionId: z.uuid(),
      revision: z.string(),
      sentAt: z.string(),
      readAt: z.string().nullable(),
    }),
  ),
  /** Кому разослана каждая ревизия — для того, кто рассылает */
  revisions: z.array(
    z.object({
      revisionId: z.uuid(),
      revision: z.string(),
      status: z.string(),
      rows: z.array(DistributionRow),
    }),
  ),
});
export type SetDistribution = z.infer<typeof SetDistribution>;

/* ─────────── Проверка комплекта по маршруту (ПД-25) ─────────── */

export const SHEET_REVIEW_RESULTS = {
  accepted: 'Принят',
  accepted_remarks: 'Принят с замечаниями',
  rejected: 'Отклонён',
} as const;
export type SheetReviewResult = keyof typeof SHEET_REVIEW_RESULTS;

export const ReviewRouteInput = z.object({
  name: z.string().trim().min(2).max(120),
  /** Для каких марок и стадий маршрут запускается сам; пусто — для всех */
  marks: z.array(z.string().trim().min(1).max(10)).max(50).default([]),
  stages: z.array(z.string().trim().min(1).max(10)).max(10).default([]),
  steps: z
    .array(
      z.object({
        name: z.string().trim().min(2).max(80),
        /** «Любые N из назначенных» */
        required: z.number().int().min(1).max(20),
        reviewerIds: z.array(z.uuid()).min(1).max(20),
      }),
    )
    .min(1)
    .max(10),
});
export type ReviewRouteInput = z.input<typeof ReviewRouteInput>;

export const ReviewRouteView = z.object({
  id: z.uuid(),
  name: z.string(),
  marks: z.array(z.string()),
  stages: z.array(z.string()),
  active: z.boolean(),
  steps: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      required: z.number(),
      reviewers: z.array(z.object({ id: z.uuid(), fullName: z.string() })),
    }),
  ),
});
export type ReviewRouteView = z.infer<typeof ReviewRouteView>;

export const RevisionReviewView = z.object({
  routeName: z.string(),
  status: z.enum(['running', 'passed', 'rejected']),
  currentStepId: z.uuid().nullable(),
  steps: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      required: z.number(),
      reviewers: z.array(z.object({ id: z.uuid(), fullName: z.string() })),
      state: z.enum(['waiting', 'current', 'done', 'rejected']),
    }),
  ),
  sheets: z.array(
    z.object({
      sheetId: z.uuid(),
      number: z.string(),
      name: z.string(),
      /** Итог листа по шагам: passed, rejected, pending */
      outcome: z.record(z.string(), z.enum(['passed', 'rejected', 'pending'])),
      results: z.array(
        z.object({
          stepId: z.uuid(),
          userId: z.uuid(),
          fullName: z.string(),
          result: z.enum(Object.keys(SHEET_REVIEW_RESULTS) as [SheetReviewResult, ...SheetReviewResult[]]),
          comment: z.string().nullable(),
          at: z.string(),
        }),
      ),
    }),
  ),
  /** Я проверяю текущий шаг */
  canReview: z.boolean(),
});
export type RevisionReviewView = z.infer<typeof RevisionReviewView>;

export const SheetReviewInput = z.object({
  results: z
    .array(
      z.object({
        sheetId: z.uuid(),
        result: z.enum(Object.keys(SHEET_REVIEW_RESULTS) as [SheetReviewResult, ...SheetReviewResult[]]),
        comment: z.string().trim().max(2000).nullable().optional(),
      }),
    )
    .min(1)
    .max(2000),
});
export type SheetReviewInput = z.infer<typeof SheetReviewInput>;

/* ─────────── Поиск по ПСД (ПД-20) ─────────── */

/** Найденное: комплект или лист по шифру и наименованию, файл — по тексту листа (надписи, штамп, спецификации) */
export const PsdSearchHit = z.object({
  kind: z.enum(['set', 'sheet', 'text']),
  projectId: z.uuid(),
  projectCode: z.string(),
  setId: z.uuid(),
  setCode: z.string(),
  setName: z.string(),
  sheetId: z.uuid().nullable(),
  sheetNumber: z.string().nullable(),
  sheetName: z.string().nullable(),
  revisionId: z.uuid().nullable(),
  revision: z.string().nullable(),
  revisionStatus: z.string().nullable(),
  fileId: z.uuid().nullable(),
  fileName: z.string().nullable(),
  page: z.number().int().nullable(),
  /** Отрывок текста с найденным словом в «»; суммы скрыты по уровню денег */
  snippet: z.string().nullable(),
});
export type PsdSearchHit = z.infer<typeof PsdSearchHit>;

export const PsdSearchResult = z.object({
  hits: z.array(PsdSearchHit),
  /** PDF, текст которых ещё читается (поиск по ним появится позже) и сканы без текстового слоя */
  pending: z.number().int(),
  scans: z.number().int(),
});
export type PsdSearchResult = z.infer<typeof PsdSearchResult>;

/* ─────────── Многостраничный PDF — по листам (ПД-22) ─────────── */

export const PsdSplitView = z.object({
  pages: z.number().int(),
  /** Номеров листов не прочитано, но страниц столько же, сколько листов, — разложено по порядку */
  byOrder: z.boolean(),
  rows: z.array(
    z.object({
      page: z.number().int(),
      number: z.string().nullable(),
      sheetId: z.uuid().nullable(),
      /** Шифр комплекта в тексте страницы; null — страница без текста */
      codeOk: z.boolean().nullable(),
      changeNo: z.string().nullable(),
      blank: z.boolean(),
      current: z.uuid().nullable(),
    }),
  ),
  sheets: z.array(z.object({ id: z.uuid(), number: z.string(), name: z.string() })),
  issues: z.array(z.string()),
  editable: z.boolean(),
});
export type PsdSplitView = z.infer<typeof PsdSplitView>;

export const PsdSplitInput = z.object({
  assign: z
    .array(
      z.object({
        page: z.number().int().min(1).max(10000),
        sheetId: z.uuid(),
        changeNo: z.string().trim().max(20).nullable().optional(),
      }),
    )
    .min(1)
    .max(2000),
});
export type PsdSplitInput = z.infer<typeof PsdSplitInput>;

/* ─────────── Дерево ПСД по объектам: пятно → раздел (марка) → альбом (поручение владельца 2.11.2026) ─────────── */

/** Узел дерева объектов проекта (пятно, здание, сооружение) — для папок ПСД */
export const PsdTreeObject = z.object({
  id: z.string(),
  parentId: z.string().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  sortOrder: z.number().int(),
});
export type PsdTreeObject = z.infer<typeof PsdTreeObject>;

export const PsdTree = z.object({
  projectId: z.string(),
  projectCode: z.string(),
  objects: z.array(PsdTreeObject),
  sets: z.array(SetSummary),
  /** Может ли человек загружать и переносить альбомы */
  canUpload: z.boolean(),
  /** Ссылки из папки объекта: сметы и график — только с правом на них */
  canEstimates: z.boolean(),
  canSchedule: z.boolean(),
});
export type PsdTree = z.infer<typeof PsdTree>;

/** Перенос альбома в другой объект (папку) — с записью в журнал ревизии и историю */
export const SetMoveInput = z.object({
  objectId: z.uuid().nullable(),
  comment: z.string().trim().max(500).nullable().optional(),
});
export type SetMoveInput = z.infer<typeof SetMoveInput>;

/** Загрузка папкой или архивом: путь файла внутри папки (папки и имя) */
export const PsdPath = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .refine((v) => !v.split(/[\\/]/).includes('..'), 'Путь не должен выходить из папки');

export const PsdUploadPlanInput = z.object({ paths: z.array(PsdPath).min(1).max(2000) });

/** Куда ляжет файл: в найденный альбом, в новый альбом по шифру или в карантин (ПД-23) */
export const PsdPlacement = z.object({
  path: z.string(),
  code: z.string(),
  action: z.enum(['existing', 'new', 'quarantine', 'skip']),
  setId: z.string().nullable(),
  objectId: z.string().nullable(),
  objectName: z.string().nullable(),
  mark: z.string().nullable(),
  /** Почему файл в карантине или пропущен */
  problems: z.array(z.string()),
});
export type PsdPlacement = z.infer<typeof PsdPlacement>;

export const PsdDropResult = z.object({
  path: z.string(),
  setId: z.string(),
  setCode: z.string(),
  revision: z.string(),
  quarantine: z.boolean(),
  sheetNumber: z.string().nullable(),
  /** Такой же файл уже есть в ревизии альбома — повторно не добавлен */
  duplicate: z.boolean(),
});
export type PsdDropResult = z.infer<typeof PsdDropResult>;

export const PsdArchiveResult = z.object({
  placed: z.array(PsdDropResult),
  skipped: z.array(z.object({ path: z.string(), reason: z.string() })),
});
export type PsdArchiveResult = z.infer<typeof PsdArchiveResult>;

/* ─────────── Влияние ревизии (ПД-08) по связям (ПД-09) ─────────── */

/** Что затрагивают изменённые листы ревизии; null — у смотрящего нет права на раздел. Без сумм. */
export const RevisionImpact = z.object({
  revisionId: z.string(),
  revision: z.string(),
  /** Первый выпуск комплекта: затронуто всё, что с ним связано */
  first: z.boolean(),
  sheets: z.array(z.object({ id: z.string(), number: z.string(), name: z.string() })),
  estimateItems: z
    .array(
      z.object({
        id: z.string(),
        number: z.number().int(),
        name: z.string(),
        estimateId: z.string(),
        estimateCode: z.string(),
        estimateName: z.string(),
        sheetNumber: z.string().nullable(),
      }),
    )
    .nullable(),
  activities: z
    .array(z.object({ id: z.string(), code: z.string(), name: z.string(), scheduleId: z.string() }))
    .nullable(),
  requests: z
    .array(z.object({ id: z.string(), number: z.string(), title: z.string(), stage: z.string() }))
    .nullable(),
  inspections: z
    .array(
      z.object({
        id: z.string(),
        number: z.string(),
        workName: z.string(),
        status: z.enum(['called', 'accepted']),
        sheetNumber: z.string().nullable(),
      }),
    )
    .nullable(),
  openRemarks: z.number().int().nullable(),
  openQuestions: z.number().int().nullable(),
});
export type RevisionImpact = z.infer<typeof RevisionImpact>;
